import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RecordingIndicator } from '@streamr/tv-ui';
import { useRef } from 'react';
import styled from 'styled-components';
import { RecordingService } from '../../../services/recording/RecordingService';
import { assert } from '../../../utils/assert';
import { BottomLeft } from '../../../utils/popups';
import { PopupCssPreset } from '../../popups/Popup.styles';
import { showStartRecordingPopup } from '../../popups/recording/StartRecordingPopup';
import { showStopRecordingPopup, } from '../../popups/recording/StopRecordingPopup';
import { showToast } from '../../popups/ToastPopup';
const mobilePositioningForStopRecordingPopup = {
    fullscreenOnly: true,
};
const desktopPositioningForStopRecordingPopup = {
    fullscreenOnly: false,
    translateFn: BottomLeft,
    popupCssPresetForDesktop: PopupCssPreset.AnchoredBottomRight,
};
export const RecordingStatusIndicator = ({ isMobile, isBroadcasting, broadcast, }) => {
    const anchorRef = useRef(null);
    const activeRecording = RecordingService.getOutput().useActiveRecording();
    const isAvailable = RecordingService.getOutput().useIsAvailable();
    const uploads = RecordingService.getOutput().useUploads();
    const onStopRecording = async () => {
        assert(activeRecording, 'Recording is not active');
        const shouldStop = await showStopRecordingPopup({
            anchorRef,
            positioning: isMobile
                ? mobilePositioningForStopRecordingPopup
                : desktopPositioningForStopRecordingPopup,
            broadcast,
            isPublic: activeRecording.visibility === 'public',
        });
        if (shouldStop) {
            RecordingService.getNotificationInterface().notifyStopRecording();
        }
    };
    const onCancelInitialization = () => {
        RecordingService.getNotificationInterface().notifyStopRecording();
    };
    const onRetry = () => {
        RecordingService.getNotificationInterface().notifyRetryInitialization();
    };
    const onButtonClick = async () => {
        if (!isAvailable) {
            showToast('Recording is not available here');
            return;
        }
        if (!isBroadcasting) {
            showToast('You need to Go Live first to start recording');
            return;
        }
        if (!activeRecording) {
            const access = await showStartRecordingPopup({
                anchorRef,
                variant: isMobile ? 'mobile' : 'desktop',
            });
            if (access) {
                RecordingService.getNotificationInterface().notifyStartRecording(
                /**
                 * @todo This is incorrect. `broadcast` is not a full Broadcast object,
                 *       but RecordingService expects it to be. Either make RecordingService
                 *       accept a partial Broadcast, or fetch the full Broadcast object here!
                 */
                broadcast, access);
            }
            return;
        }
        if (activeRecording.status === 'recording') {
            // biome-ignore lint/nursery/noFloatingPromises: TODO
            onStopRecording();
        }
        else if (activeRecording.status === 'initializing') {
            onCancelInitialization();
        }
        else if (activeRecording.status === 'error') {
            onRetry();
        }
    };
    return (_jsxs(Root, { ref: anchorRef, children: [_jsx(RecordingIndicator, { status: activeRecording?.status ?? 'not_recording', onButtonClick: onButtonClick, shape: isMobile ? 'circle' : 'square', title: activeRecording?.status === 'error'
                    ? (activeRecording.message ?? 'Record')
                    : 'Record' }), uploads.length > 0 && (_jsx(UploadNotice, { "data-automation-id": "recording-upload-notice", title: "The recording is uploaded in the background. It is safe to go on.", children: formatUploads(uploads) }))] }));
};
function formatUploads(uploads) {
    const recorded = uploads.reduce((sum, u) => sum + u.recordedBytes, 0);
    const uploaded = uploads.reduce((sum, u) => sum + u.uploadedBytes, 0);
    const percent = recorded > 0 ? Math.floor((uploaded / recorded) * 100) : 0;
    if (uploads.some((u) => u.error !== undefined)) {
        return 'Recording upload failed';
    }
    if (uploads.every((u) => u.paused)) {
        return `Recording waits for upload · ${percent}%`;
    }
    return `Uploading recording · ${percent}%`;
}
const Root = styled.div `
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
`;
const UploadNotice = styled.span `
    font-size: 0.75rem;
    line-height: 1;
    padding: 0.4rem 0.6rem;
    border-radius: 1rem;
    white-space: nowrap;
    background: var(--tv-ui-color-15);
    color: var(--tv-ui-color-3);
    font-variant-numeric: tabular-nums;
`;
